import type { CSSProperties } from 'react';

import { COACHMARK_HIGHLIGHT_CLASS } from 'src/components/Coachmark';
import { CoachmarkTourKey } from 'src/store/ui/types';
import { pollForElement } from 'src/utils/DomUtils';

// A client row's actions button (the Actions column is pinned right).
const getRowActionsSelector = (rowIndex: number): string =>
  `[row-index="${rowIndex}"] [data-testid="more-actions-menu"]`;
const FIRST_ROW_SELECTOR = '.ag-row[row-index="0"]';
const OPEN_MENU_ITEM_SELECTOR = `[class~="${COACHMARK_HIGHLIGHT_CLASS}"]`;
const MENU_REOPEN_POLL_MS = 200;
// Gap between a coachmark card and the menu item it points at.
export const MENU_STEP_OFFSET = 24;
// The prototype's gap under the menu, which lands the arrow's tip on its edge.
export const UNDER_MENU_OFFSET = 10;
export const CRM_CLIENTS_TOUR_KEY = 'crmClients' satisfies CoachmarkTourKey;

// Phone cards are narrower than desktop's and keep a 16px gutter. Their
// placement is locked, so they can't shift away from a viewport edge on their own.
export const MOBILE_CARD_STYLE: CSSProperties = {
  width: 'min(260px, calc(100vw - 32px))',
};

/**
 * The row menu the coachmark opened. On phones there's no room beside it, so
 * the card sits under it instead.
 */
export const getOpenCoachmarkMenu = (): HTMLElement | null => {
  const menu = document
    .querySelector(OPEN_MENU_ITEM_SELECTOR)
    ?.closest('.MuiMenu-paper');
  return menu instanceof HTMLElement ? menu : null;
};

export const enterCoachmarkRowStep = (): (() => void) => {
  let highlighted: HTMLElement[] = [];

  const cancelPoll = pollForElement(
    () => {
      const rows = Array.from(
        document.querySelectorAll(FIRST_ROW_SELECTOR),
      ).filter((row): row is HTMLElement => row instanceof HTMLElement);
      return rows.length > 0 ? rows : null;
    },
    (rows) => {
      rows.forEach((row) => {
        row.classList.add(COACHMARK_HIGHLIGHT_CLASS);
      });
      highlighted = rows;
    },
  );

  return () => {
    cancelPoll();
    highlighted.forEach((row) => {
      row.classList.remove(COACHMARK_HIGHLIGHT_CLASS);
    });
  };
};

// The coachmark menu turns off Escape and click-away, and only a menu item
// closes it, so a step dismissed any other way closes it through its backdrop.
export const closeCoachmarkMenu = (): void => {
  const backdrop = document
    .querySelector(OPEN_MENU_ITEM_SELECTOR)
    ?.closest('.MuiMenu-root')
    ?.querySelector('.MuiBackdrop-root');
  if (backdrop instanceof HTMLElement) {
    backdrop.click();
  }
};

export const createEnterCoachmarkMenuStep =
  (
    setActive: (active: boolean) => void,
    isActive: () => boolean,
    rowIndex = 0,
  ) =>
  (): (() => void) => {
    setActive(true);
    const rowActionsSelector = getRowActionsSelector(rowIndex);

    const openMenu = (): void => {
      const button = document.querySelector(rowActionsSelector);
      if (button instanceof HTMLElement) {
        button.click();
      }
    };

    const frame = requestAnimationFrame(openMenu);

    // While the tour points at it, the menu renders non-modal (see
    // ClientActionRenderer): click-away no longer closes it and the page behind
    // stays usable, so no dismiss guard is needed here. This backstop only covers a
    // close the tour doesn't expect (e.g. a programmatic one), reopening the menu so
    // the step stays completable. It no-ops while the menu is open — the norm.
    const reopenInterval = setInterval(() => {
      if (isActive() && !document.querySelector(OPEN_MENU_ITEM_SELECTOR)) {
        openMenu();
      }
    }, MENU_REOPEN_POLL_MS);

    return () => {
      cancelAnimationFrame(frame);
      clearInterval(reopenInterval);
      setActive(false);
      // The menu was opened programmatically (button.click), so the button's
      // active highlight never clears via mouse-leave/click-away — and completing
      // the tour via "Open portal" opens a new tab that steals focus before
      // click-away could fire. Nudge the button's own mouse-leave path so it does
      // not stay highlighted after the tour ends.
      const button = document.querySelector(rowActionsSelector);
      button?.dispatchEvent(
        new MouseEvent('mouseout', {
          bubbles: true,
          relatedTarget: document.body,
        }),
      );
    };
  };
